import { isPlainObject, sortBy } from "lodash-es";
import { combineLatest, first, map, Observable } from "rxjs";
import {
  ContractTypeOf,
  getPayoutPlanDuration,
  Product,
  PrognosisTypeOf,
} from "src/app/models/engagements/generic-engagement.model";
import { PublicPensionPrognosisParametersWithStartPayoutAge } from "src/app/services/api/public-pension-prognosis-parameters-dynamic.service";
import * as Graph from "src/app/services/api/savings-and-pension-queries.types";
import { PublicPensionContractStatus } from "src/app/services/prognoses-services/public-pension.service.vars";
import { toArrayOrEmpty } from "src/app/utils/array";
import { Boundary } from "src/app/utils/getNumberWithinBounds";
import { isTypeNumber } from "src/app/utils/number";
import { select } from "src/app/utils/rxjs/select";
import { Nullable } from "src/app/utils/utils";
import { EngagementName, EngagementSimulationStatus } from "../pension.model";
import { PAYOUT_DURATIONS, PayoutDuration } from "./generic-engagement.model";
import { GenericGraphEngagement, getAverageAnnualPayout } from "./generic-graph-engagement.model";

export type Contract = Pick<
  Graph.PublicPensionContract,
  "contractNumber" | "coverages" | "employer" | "id" | "productNameFmsKey" | "status" | "contractNavigation"
>;

export const PUBLIC_PENSION_ENGAGEMENT_ID = "OffentligStb";

export class PublicPensionEngagement extends GenericGraphEngagement<
  Contract[],
  Graph.PublicPensionPrognosis,
  Graph.PublicPensionPrognosisInput
> {
  public constructor(
    public readonly prognosisParameters: PublicPensionPrognosisParametersWithStartPayoutAge,
    contract: ContractTypeOf<PublicPensionEngagement>,
    prognosis?: PrognosisTypeOf<PublicPensionEngagement>,
  ) {
    super(contract, prognosis);
  }

  public isActive(): boolean {
    return this.getContracts().some((contract) => contract.status === PublicPensionContractStatus.Active);
  }

  public getContracts(): Contract[] {
    return this.contract;
  }

  public getIdentifier(): string {
    return PUBLIC_PENSION_ENGAGEMENT_ID;
  }

  /** @deprecated Use getNameAsync instead */
  public getName(age?: number): EngagementName {
    return select(this.getNameAsync(age));
  }

  public getNameAsync(age?: number | undefined): Observable<EngagementName> {
    const sortedContracts = sortBy(
      this.getContracts(),
      (contract) => contract.status === PublicPensionContractStatus.Active,
    );

    const name$ = this.getFirstContractName().pipe(
      map((name) => (this.getIsAfpPayoutAtAge(age) ? name.concat(" (AFP)") : name)),
    );

    return combineLatest([
      name$,
      this.getFirstContractDescription(sortedContracts),
      this.getFirstContractPayer(sortedContracts),
    ]).pipe(
      first(),
      map(([name, description, payer]) => ({
        name,
        description,
        payer,
        supplier: this.isActive() ? "Storebrand" : undefined,
      })),
    );
  }

  /**
   * In addition to getProduct() in super - Public pension has multiple contacts on same engagement -
   * To be able to create dyanmic urls with unique tracking parameters - each contract need to be returend
   */
  public getProducts(): Nullable<Product[]> {
    const contracts = this.getContracts();
    const products: Product[] = [];
    contracts.forEach((contract) => {
      products.push({ productCode: (contract as any)?.productCode ?? undefined });
    });

    return products;
  }

  public getPayoutPlan(): Graph.PayoutPlan[] {
    return ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPension)).filter(isPlainObject);
  }

  public getPaymentPlanPaaslag(): Graph.PayoutPlan[] {
    return ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPaaslag)).filter(isPlainObject);
  }

  public getPaymentPlanPension(): Graph.PayoutPlan[] {
    return ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPension)).filter(isPlainObject);
  }

  public getPaymentPlanPensionGross(): Graph.PayoutPlan[] {
    return ([] as Graph.PayoutPlan[])
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanPensionGross))
      .filter(isPlainObject);
  }

  /**
   * Concatinates all possible AFP payout plans into one PayoutPlan list
   * @returns A concated list of all possible AFP payout plans - Will always only be one type (62, 63 or 65)
   */
  public getPaymentPlanAfp(): Graph.PayoutPlan[] {
    return ([] as Graph.PayoutPlan[])
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp62))
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp65))
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp1963))
      .filter(isPlainObject);
  }

  public override getPaaslagAveragePayout(): number | null {
    const payoutPlan = this.getPaymentPlanPaaslag();
    return getAverageAnnualPayout(payoutPlan);
  }

  public override getPensionAveragePayout(): number | null {
    const payoutPlan = this.getPaymentPlanPension();
    return getAverageAnnualPayout(payoutPlan);
  }

  public override getGrossAveragePayout(): number | null {
    const payoutPlan = this.getPaymentPlanPensionGross();
    return getAverageAnnualPayout(payoutPlan);
  }

  public override getAfpAveragePayout(): number | null {
    const payoutPlan = this.getPaymentPlanAfp();
    return getAverageAnnualPayout(payoutPlan);
  }

  public getPayoutFromAgeAfp(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[])
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp62))
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp65))
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp1963))
        .filter(isPlainObject)?.[0]?.age || undefined
    );
  }

  public getPayoutToAgeAfp(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[])
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp62))
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp65))
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp1963))
        .filter(isPlainObject)?.[this.getPaymentPlanAfp().length - 1]?.age || undefined
    );
  }

  public getPayoutFromAgePaaslag(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPaaslag)).filter(isPlainObject)?.[0]
        ?.age || undefined
    );
  }

  public getPayoutToAgePaaslag(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPaaslag)).filter(isPlainObject)?.[
        this.getPaymentPlanPaaslag().length - 1
      ]?.age || undefined
    );
  }

  public getPayoutFromAgeGross(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[])
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanPensionGross))
        .filter(isPlainObject)?.[0]?.age || undefined
    );
  }

  public getPayoutToAgeGross(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[])
        .concat(toArrayOrEmpty(this.prognosis?.paymentPlanPensionGross))
        .filter(isPlainObject)?.[this.getPaymentPlanPensionGross().length - 1]?.age || undefined
    );
  }

  public getPayoutFromAgePensionPlan(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPension)).filter(isPlainObject)?.[0]
        ?.age || undefined
    );
  }

  public getPayoutToAgePensionPlan(): number | undefined {
    return (
      ([] as Graph.PayoutPlan[]).concat(toArrayOrEmpty(this.prognosis?.paymentPlanPension)).filter(isPlainObject)?.[
        this.getPaymentPlanPension().length - 1
      ]?.age || undefined
    );
  }

  /** @deprecated Use explicit prognosis getters in stead - ie, getPayoutToAgeAfp()*/
  public getPayoutFromAge(): number | undefined {
    return this.getPayoutPlan()?.[0]?.age || undefined;
  }

  /** @deprecated Use explicit prognosis getters in stead - ie, getPayoutToAgeAfp()*/
  public getPayoutToAge(): number | undefined {
    const payoutPlan = this.getPayoutPlan();
    if (payoutPlan && Array.isArray(payoutPlan) && payoutPlan.length) {
      return payoutPlan[payoutPlan.length - 1]?.age || undefined;
    }
    return undefined;
  }

  public getSimulationStatus(): EngagementSimulationStatus[] {
    const DEFAULT_SIMULATION_STATUSES = [{ severity: Graph.SimulationSeverity.Ok }];
    return this.prognosis?.statusMessages?.map(mapStatusMessageToSimulationStatus) ?? DEFAULT_SIMULATION_STATUSES;
  }

  public getFirstYearPayout(): number {
    const firstYear = this.getPayoutPlan()?.[0];
    return firstYear?.amount ?? 0;
  }

  // Stop using this
  public getPayoutDuration(): PayoutDuration {
    return PAYOUT_DURATIONS.infinite;
  }

  public getPayoutDurationFromAge(toAge: number | undefined): PayoutDuration {
    if (!toAge || this.isLifelongPayoutFromAge(toAge)) {
      return PAYOUT_DURATIONS.infinite;
    }

    return getPayoutPlanDuration(this.getPayoutPlan());
  }

  // PublicPension has 93 as cap age for lifelong payout
  public isLifelongPayoutFromAge(age: number): boolean {
    return age >= 93;
  }

  public isLifelongPayout(): boolean {
    return true;
  }

  public isSavingsEngagement(): boolean {
    return false;
  }

  public getPayoutAgeRangeBoundary(): Boundary | undefined {
    if (
      !isTypeNumber(this.prognosisParameters.publicPensionPrognosisParameters.minimumAgeWithdrawal) ||
      !isTypeNumber(this.prognosisParameters.publicPensionPrognosisParameters.maximumAgeWithdrawal)
    ) {
      return undefined;
    }

    return {
      floor: this.prognosisParameters.publicPensionPrognosisParameters.minimumAgeWithdrawal,
      ceil: this.prognosisParameters.publicPensionPrognosisParameters.maximumAgeWithdrawal,
    };
  }

  public hasFolketrygdPrognosis(): boolean {
    return !!(
      this.prognosisParameters?.publicPensionPrognosisParameters?.hasPublicPensionEngagements ||
      this.prognosisParameters?.publicPensionPrognosisParameters?.earnedPensionRight
    );
  }

  public override getAllocationCode(): Nullable<string> {
    return undefined;
  }

  public getCustomerPersonStatus(): Nullable<string> {
    return undefined;
  }

  private getFirstContract(
    contracts?: Graph.PublicPensionContract[],
  ): Graph.PublicPensionContract | Contract | undefined {
    return (contracts || this.getContracts())?.[0];
  }

  private getFirstContractProductNameFmsKey(contracts?: Graph.PublicPensionContract[]): string | undefined {
    const firstContract = this.getFirstContract(contracts);

    return firstContract?.productNameFmsKey ?? undefined;
  }

  private getFirstContractDescription(contracts?: Graph.PublicPensionContract[]): Observable<string> {
    const fmsService = this.getFmsService();
    const productNameFmsKey = this.getFirstContractProductNameFmsKey(contracts);

    return fmsService.translateAsync(`${productNameFmsKey}.description`, {
      logEmpty: false,
    });
  }

  private getFirstContractName(contracts?: Graph.PublicPensionContract[]): Observable<string> {
    const fmsService = this.getFmsService();
    const productNameFmsKey = this.getFirstContractProductNameFmsKey(contracts);

    return fmsService.translateAsync<string>(productNameFmsKey);
  }

  private getFirstContractPayer(contracts?: Graph.PublicPensionContract[]): Observable<string> {
    const fmsService = this.getFmsService();
    return fmsService.translateAsync<string>("publicPensionGraphEngagement.others").pipe(
      first(),
      map((othersLabel) => {
        const firstContract = this.getFirstContract(contracts);
        const isMultipleContracts = (contracts?.length ?? 0) > 1;
        const multipleContractsLabel = isMultipleContracts ? ` ${othersLabel}` : "";
        const employerName = firstContract?.employer?.name;

        return employerName + multipleContractsLabel;
      }),
    );
  }

  private getIsAfpPayoutAtAge(age?: number): boolean {
    if (!isTypeNumber(age) || age > 66) {
      return false;
    }
    return ([] as Graph.PayoutPlan[])
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp62))
      .concat(toArrayOrEmpty(this.prognosis?.paymentPlanAfp65))
      .some((el) => el?.age === age && isTypeNumber(el.amount) && el?.amount > 0);
  }
}

export function mapStatusMessageToSimulationStatus(
  status: Graph.PublicPensionStatusMessage,
): EngagementSimulationStatus {
  type StatusType = "S" | "F" | "A" | "I";
  const mapStatusTypeToSeverity = (statusType: StatusType): Graph.SimulationSeverity => {
    switch (statusType) {
      case "F":
        return Graph.SimulationSeverity.SimulationError;
      case "S":
        return Graph.SimulationSeverity.NotAllowed;
      case "A":
        return Graph.SimulationSeverity.Notice;
      case "I":
      case null:
      case undefined:
      default:
        return Graph.SimulationSeverity.Ok; //TODO: can this be something else? The type should really come from the graph.
    }
  };

  const severity = mapStatusTypeToSeverity(status.messageType as StatusType);
  const key = `sharedSimulationMessages.${status.messageKey}`;
  return { severity, key };
}
